import React, { useMemo } from 'react'

import {
  ArrowTurnDownRightIcon,
  EnvelopeIcon,
  HomeIcon,
  LinkIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline'
import { Form, Formik } from 'formik'
import { useRouter } from 'next/router'
import { toast } from 'react-toastify'

import { useCheckHasAlert } from '../../components/Alertsv2/useCheckHasAlert'
import { AlertFlag } from '../AlertFlag/AlertFlag'
import CallPhone from '@/components/CallPhone'
import { InputField, SelectField } from '@/components/FormikInputs'
import { ProfileTypes, TitleTypeOptions } from '@/constants/applicationProfile'
import { CustomerMagicLinkButton } from '@/features/FileContacts/CustomerMagicLinkButton'
import { useNameManager } from '@/features/FileContacts/hooks/contactNameManager'
import useCardsCollapseStatus from '@/features/FileContacts/useCardsCollapseStatus'
import {
  formatFullAddress,
  generateMarriedOptions,
  serializeNameDetails,
} from '@/features/FileContacts/utils'
import { Application } from '@/graphql/generated'
import { useGetApplicationQuery } from '@/graphql/services/application'
import { useUpdateApplicantProfileMutation } from '@/services/application'
import { useUpdateProfileMutation } from '@/services/applicationProfile'
import { useGetLatestTitleReportMetadataQuery } from '@/services/documentMetadata'
import {
  useChangeContactTypeMutation,
  useListApplicationProfilesQuery,
  useListNonApplicantsQuery,
} from '@/services/v2-application/applicationProfile'
import { ApplicationProfile } from '@/types/unlock'
import { toastDefaultOptions } from '@/utils/toast'

interface ApplicantCardProps {
  title: string
  applicationProfile: ApplicationProfile
  application: Application
  handleSendEmail: (recipient: string) => void
  isCoApplicant: boolean
}

export const ApplicantCard = ({
  title,
  applicationProfile,
  application,
  handleSendEmail,
  isCoApplicant,
}: ApplicantCardProps) => {
  const router = useRouter()
  const { id } = router.query
  const { isExpanded, toggleIsExpanded } = useCardsCollapseStatus(true)

  const [updateApplicantProfile] = useUpdateApplicantProfileMutation()
  const [updateProfile] = useUpdateProfileMutation()

  const { data: applicationProfilesData } = useListApplicationProfilesQuery(application?.uuid, {
    skip: !application?.uuid,
  })
  const applicationProfiles = useMemo(
    () => applicationProfilesData?.results,
    [applicationProfilesData]
  )
  const { data: titleReportMetadata } = useGetLatestTitleReportMetadataQuery(application?.uuid, {
    skip: !application?.uuid,
  })
  const { data: nonApplicantsData } = useListNonApplicantsQuery(application?.uuid, {
    skip: !application?.uuid,
  })
  const { data: applicationData } = useGetApplicationQuery(
    { externalId: id as string },
    { skip: !id }
  )

  const { ownerNamesOptions, onUpdateName } = useNameManager(
    applicationProfile,
    titleReportMetadata,
    updateProfile
  )

  const profileFullName = applicationProfile?.profile?.full_name?.toUpperCase()?.trim() || ''
  const { key: alertKeyTitleVestingNameMismatch, hasAlert: hasAlertTitleVestingNamesMismatch } =
    useCheckHasAlert({
      key: `suspicious-data-title-vesting-names-mismatch-applicant-${profileFullName}`,
    })

  const nonApplicants = useMemo(() => nonApplicantsData?.results, [nonApplicantsData])

  const [changeContactTypeMutation, { isLoading }] = useChangeContactTypeMutation()

  const marriedToOptions = useMemo(() => {
    return generateMarriedOptions(applicationProfile, applicationProfiles, nonApplicants)
  }, [applicationProfiles, nonApplicants])

  const fullAddress = formatFullAddress(applicationProfile?.profile?.mailing_address)

  const onUpdateAppProfile = (values) => {
    if (values.married_to_profile !== initialValues.married_to_profile) {
      updateApplicantProfile({
        uuid: applicationProfile.uuid,
        married_to_profile: eval(values.married_to_profile),
      })
        .unwrap()
        .then(() => {
          toast.success('The profile has been successfully updated.', { ...toastDefaultOptions })
        })
        .catch((error) => {
          const message = error?.data ? error?.data?.[0] : 'Something went wrong'
          toast.error(message, { ...toastDefaultOptions })
        })
    }
    if (values.title_type !== initialValues.title_type) {
      updateApplicantProfile({
        uuid: applicationProfile.uuid,
        title_type: values.title_type,
      })
    }
  }

  const initialValues = {
    email: applicationProfile.profile.email || '',
    phone: applicationProfile.profile.phone || '',
    full_address: applicationProfile?.profile?.mailing_address ? fullAddress : '',
    married_to_profile: applicationProfile?.married_to_profile,
    type: applicationProfile?.profile_type,
    full_name: serializeNameDetails(applicationProfile?.profile) || '',
    address: {
      street: applicationProfile?.profile?.mailing_address?.street || '',
      unit_number: applicationProfile?.profile?.mailing_address?.unit_number || '',
      city: applicationProfile?.profile?.mailing_address?.city || '',
      state: applicationProfile?.profile?.mailing_address?.state || '',
      zip_code: applicationProfile?.profile?.mailing_address?.zip_code || '',
    },
    title_type: applicationProfile?.title_type || '',
  }

  const nameFlags = []
  if (hasAlertTitleVestingNamesMismatch) {
    nameFlags.push(alertKeyTitleVestingNameMismatch)
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => {}}
      enableReinitialize
    >
      {({ values }) => (
        <Form>
          <div className='card bg-base-100 shadow-md flex flex-col border'>
            <div
              onClick={() => toggleIsExpanded()}
              className={`w-full gap-2 flex justify-between px-5 py-2 ${
                isExpanded && 'bg-[#24d3ee]'
              } transition-colors duration-500 rounded-t-box  cursor-pointer`}
            >
              {isCoApplicant ? (
                <div className='w-full flex flex-col text-start'>
                  <SelectField
                    extraFieldClasses={`${isExpanded && 'bg-[#24d3ee]'} ${
                      !isExpanded && 'pointer-events-none'
                    } transition-colors duration-500 !text-black !pl-0 !pt-0 !pb-0`}
                    extraClasses='w-full'
                    placeholder='Contact Type'
                    name='type'
                    options={[
                      { label: 'Co-Applicant', value: ProfileTypes.APPLICANT },
                      { label: 'Co-Owner', value: ProfileTypes.CO_OWNER },
                      { label: 'NOO', value: ProfileTypes.NOO },
                    ]}
                    showError
                    onCustomClick={(e) => {
                      if (isExpanded) e.stopPropagation()
                    }}
                    onChange={async (e) => {
                      try {
                        await changeContactTypeMutation({
                          applicationUuid: application.uuid,
                          applicantProfileUuid: `${applicationProfile.uuid}`,
                          new_type: e.target.value,
                        })
                      } catch (error) {
                        console.error(error)
                      }
                    }}
                    disabled={isLoading || values.full_name === ''}
                  />
                </div>
              ) : (
                <div className='flex flex-col text-start'>
                  <p className='text-base font-bold'>{title}</p>
                </div>
              )}
              <div className='flex items-center gap-2'>
                {!isExpanded && (
                  <>
                    <div
                      className='tooltip tooltip-left'
                      data-tip={`Phone: ${values.phone}`}
                    >
                      <CallPhone callTo={values.phone} />
                    </div>
                    <div
                      className='tooltip tooltip-left'
                      data-tip={`E-mail: ${values.email}`}
                    >
                      <EnvelopeIcon
                        className='w-4 cursor-pointer'
                        onClick={(e) => {
                          e.stopPropagation()
                          handleSendEmail(values?.email)
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className={`px-5 pb-2 pt-0 flex flex-col gap-1 ${isExpanded && 'pt-2'}`}>
              <div className='flex flex-row gap-1 text-xs items-center'>
                <UserCircleIcon className='w-4' />
                <SelectField
                  extraClasses='w-full'
                  placeholder='Contact name'
                  name='full_name'
                  options={ownerNamesOptions}
                  onBlur={(event) => onUpdateName(event)}
                  showError
                  disabled={!titleReportMetadata}
                />
                {nameFlags.length > 0 && (
                  <AlertFlag
                    flag={nameFlags}
                    tooltipClassName='tooltip-left'
                  />
                )}
              </div>
              {isExpanded && (
                <>
                  {applicationData?.property?.titleHeld === 'trust' &&
                  applicationData?.pricingVersion >= 22 ? (
                    <div className='flex flex-row gap-1 text-xs'>
                      <ArrowTurnDownRightIcon className='w-4' />
                      <SelectField
                        extraClasses='w-full'
                        placeholder='Title Type'
                        name='title_type'
                        options={TitleTypeOptions}
                        onBlur={() => onUpdateAppProfile(values)}
                        showError
                      />
                    </div>
                  ) : null}
                  <div className='flex flex-row gap-1 text-xs items-center'>
                    <LinkIcon className='w-4' />
                    <SelectField
                      style='minimal-input'
                      extraClasses='w-full'
                      placeholder='Married To'
                      placeholderDefaultValue={-1}
                      name='married_to_profile'
                      options={marriedToOptions}
                      onBlur={() => onUpdateAppProfile(values)}
                      showError
                    />
                  </div>
                  <div className='flex flex-row gap-1 text-xs items-center'>
                    <CallPhone callTo={values.phone} />
                    <InputField
                      name='phone'
                      placeholder='Phone'
                      parentClassName='w-full'
                      disabled
                    />
                  </div>
                  <div className='flex flex-row gap-1 text-xs'>
                    <EnvelopeIcon
                      className='w-4 cursor-pointer'
                      onClick={() => handleSendEmail(values?.email)}
                    />
                    <InputField
                      name='email'
                      placeholder='E-mail'
                      parentClassName='w-full'
                      disabled
                    />
                  </div>
                  <div className='flex flex-row gap-1 text-xs'>
                    <HomeIcon className='w-4' />
                    <InputField
                      name='full_address'
                      placeholder='Address'
                      parentClassName='w-full'
                      disabled
                    />
                  </div>
                </>
              )}
              <CustomerMagicLinkButton applicationProfile={applicationProfile} />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  )
}
